/**
 * Generic Popover Component
 * A reusable popover that renders content above everything using Modal
 * Supports animations via react-native-reanimated
 * Includes an arrow indicator pointing to the trigger element
 */

import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react'
import {
  Modal,
  Pressable,
  StyleSheet,
  View,
  type LayoutRectangle,
  type ViewStyle,
  useWindowDimensions
} from 'react-native'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'

export type PopoverSide = 'top' | 'bottom' | 'left' | 'right'
export type PopoverAlign = 'start' | 'center' | 'end'

const ARROW_SIZE = 10
const ARROW_OFFSET = 16 // Distance from edge when aligned to start/end

interface TriggerPosition {
  x: number
  y: number
  width: number
  height: number
}

interface PopoverProps {
  /** Whether the popover is visible */
  isOpen: boolean
  /** Called when popover should close (e.g., backdrop press) */
  onClose: () => void
  /** The content to display inside the popover */
  children: React.ReactNode
  /** The trigger element that opens the popover */
  trigger: React.ReactNode
  /** Called when trigger is pressed */
  onTriggerPress: () => void
  /** Which side to position the popover relative to trigger */
  side?: PopoverSide
  /** Alignment along the side axis */
  align?: PopoverAlign
  /** Offset from the trigger (in pixels) */
  sideOffset?: number
  /** Whether to close when pressing outside */
  closeOnBackdrop?: boolean
  /** Custom styles for the popover content container */
  contentStyle?: ViewStyle
  /** Custom styles for the backdrop */
  backdropStyle?: ViewStyle
  /** Animation duration in ms */
  animationDuration?: number
  /** Whether to show the arrow indicator */
  showArrow?: boolean
}

export function Popover({
  isOpen,
  onClose,
  children,
  trigger,
  onTriggerPress,
  side = 'top',
  align = 'center',
  sideOffset = 8,
  closeOnBackdrop = true,
  contentStyle,
  backdropStyle,
  animationDuration = 200,
  showArrow = true
}: PopoverProps) {
  const triggerRef = useRef<View>(null)
  const [triggerPosition, setTriggerPosition] = useState<TriggerPosition | null>(null)
  const [contentSize, setContentSize] = useState<{ width: number; height: number } | null>(null)
  const [isPositioned, setIsPositioned] = useState(false)
  const [actualSide, setActualSide] = useState<PopoverSide>(side)
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()

  // Measure trigger position when opening
  const measureAndOpen = useCallback(() => {
    if (triggerRef.current) {
      triggerRef.current.measureInWindow((x, y, width, height) => {
        // Validate measurements
        if (width > 0 && height > 0) {
          setTriggerPosition({ x, y, width, height })
          setIsPositioned(false) // Reset positioning for new measurement
          onTriggerPress()
        } else {
          // Fallback: try measure relative to parent
          triggerRef.current?.measure((_fx, _fy, measuredWidth, measuredHeight, pageX, pageY) => {
            if (measuredWidth > 0 && measuredHeight > 0) {
              setTriggerPosition({ x: pageX, y: pageY, width: measuredWidth, height: measuredHeight })
              setIsPositioned(false)
              onTriggerPress()
            } else {
              // Last resort: just open without position
              onTriggerPress()
            }
          })
        }
      })
    } else {
      onTriggerPress()
    }
  }, [onTriggerPress])

  // Re-measure when modal opens to ensure accuracy
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      // Small delay to ensure layout is stable
      const timer = setTimeout(() => {
        triggerRef.current?.measureInWindow((x, y, width, height) => {
          if (width > 0 && height > 0) {
            setTriggerPosition({ x, y, width, height })
          }
        })
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Handle content layout measurement
  const handleContentLayout = useCallback((event: { nativeEvent: { layout: LayoutRectangle } }) => {
    const { width, height } = event.nativeEvent.layout
    if (width > 0 && height > 0) {
      setContentSize({ width, height })
      setIsPositioned(true)
    }
  }, [])

  // Calculate popover position based on trigger position and side/align
  const getPositionStyle = useCallback((): ViewStyle => {
    // Not ready yet - position off-screen to measure
    const hasRequiredData = triggerPosition && contentSize
    if (!hasRequiredData) {
      return { position: 'absolute', top: -9999, left: -9999, opacity: 0 }
    }

    const { x: triggerX, y: triggerY, width: triggerWidth, height: triggerHeight } = triggerPosition
    const { width: contentWidth, height: contentHeight } = contentSize

    let top = 0
    let left = 0
    let computedSide = side

    // Account for arrow in offset
    const totalOffset = sideOffset + (showArrow ? ARROW_SIZE : 0)

    // Calculate position based on side
    switch (side) {
      case 'top':
        top = triggerY - contentHeight - totalOffset
        break
      case 'bottom':
        top = triggerY + triggerHeight + totalOffset
        break
      case 'left':
        left = triggerX - contentWidth - totalOffset
        top = triggerY + triggerHeight / 2 - contentHeight / 2
        break
      case 'right':
        left = triggerX + triggerWidth + totalOffset
        top = triggerY + triggerHeight / 2 - contentHeight / 2
        break
    }

    // Calculate alignment for top/bottom
    if (side === 'top' || side === 'bottom') {
      switch (align) {
        case 'start':
          left = triggerX
          break
        case 'center':
          left = triggerX + triggerWidth / 2 - contentWidth / 2
          break
        case 'end':
          left = triggerX + triggerWidth - contentWidth
          break
      }
    }

    // Calculate alignment for left/right
    if (side === 'left' || side === 'right') {
      switch (align) {
        case 'start':
          top = triggerY
          break
        case 'center':
          top = triggerY + triggerHeight / 2 - contentHeight / 2
          break
        case 'end':
          top = triggerY + triggerHeight - contentHeight
          break
      }
    }

    // Clamp to screen bounds with padding
    const padding = 12
    left = Math.max(padding, Math.min(left, windowWidth - contentWidth - padding))
    top = Math.max(padding, Math.min(top, windowHeight - contentHeight - padding))

    // If top side doesn't fit, flip to bottom
    if (side === 'top' && top < padding) {
      top = triggerY + triggerHeight + totalOffset
      computedSide = 'bottom'
    }

    // If bottom side doesn't fit, flip to top
    if (side === 'bottom' && top + contentHeight > windowHeight - padding) {
      top = triggerY - contentHeight - totalOffset
      computedSide = 'top'
    }

    // Update actual side for arrow positioning
    if (computedSide !== actualSide) {
      setActualSide(computedSide)
    }

    return {
      position: 'absolute',
      top,
      left,
      opacity: isPositioned ? 1 : 0
    }
  }, [
    triggerPosition,
    contentSize,
    side,
    align,
    sideOffset,
    windowWidth,
    windowHeight,
    isPositioned,
    showArrow,
    actualSide
  ])

  // Calculate arrow position relative to trigger
  const arrowStyle = useMemo((): ViewStyle => {
    const canShowArrow = triggerPosition && contentSize && showArrow
    if (!canShowArrow) {
      return { display: 'none' }
    }

    const { x: triggerX, width: triggerWidth } = triggerPosition
    const { width: contentWidth } = contentSize

    // Calculate horizontal position of arrow to point at trigger center
    let arrowLeft = 0

    // Get the content's left position (approximation based on alignment)
    let contentLeft = 0
    switch (align) {
      case 'start':
        contentLeft = triggerX
        break
      case 'center':
        contentLeft = triggerX + triggerWidth / 2 - contentWidth / 2
        break
      case 'end':
        contentLeft = triggerX + triggerWidth - contentWidth
        break
    }

    // Clamp content position
    const padding = 12
    contentLeft = Math.max(padding, Math.min(contentLeft, windowWidth - contentWidth - padding))

    // Arrow should point to trigger center
    const triggerCenter = triggerX + triggerWidth / 2
    arrowLeft = triggerCenter - contentLeft - ARROW_SIZE

    // Clamp arrow position within content bounds
    arrowLeft = Math.max(ARROW_OFFSET, Math.min(arrowLeft, contentWidth - ARROW_SIZE - ARROW_OFFSET))

    // Position based on which side the popover is on
    const baseStyle: ViewStyle = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderLeftWidth: ARROW_SIZE,
      borderRightWidth: ARROW_SIZE,
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent'
    }

    if (actualSide === 'top') {
      // Arrow points down (at bottom of popover)
      return {
        ...baseStyle,
        bottom: -ARROW_SIZE,
        left: arrowLeft,
        borderTopWidth: ARROW_SIZE,
        borderTopColor: 'rgba(255, 255, 255, 0.7)',
        borderBottomWidth: 0
      }
    }

    if (actualSide === 'bottom') {
      // Arrow points up (at top of popover)
      return {
        ...baseStyle,
        top: -ARROW_SIZE,
        left: arrowLeft,
        borderBottomWidth: ARROW_SIZE,
        borderTopColor: 'rgba(255, 255, 255, 0.7)',
        borderTopWidth: 0
      }
    }

    return { display: 'none' }
  }, [triggerPosition, contentSize, align, actualSide, showArrow, windowWidth])

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setContentSize(null)
      setIsPositioned(false)
      setActualSide(side)
    }
  }, [isOpen, side])

  return (
    <>
      {/* Trigger wrapper - must be collapsable={false} for accurate measurement */}
      <View ref={triggerRef} collapsable={false} onLayout={() => {}}>
        <Pressable onPress={measureAndOpen}>{trigger}</Pressable>
      </View>

      {/* Popover Modal */}
      <Modal visible={isOpen} transparent animationType="none" onRequestClose={onClose} statusBarTranslucent>
        {/* Backdrop */}
        <Pressable style={[styles.backdrop, backdropStyle]} onPress={closeOnBackdrop ? onClose : undefined}>
          <Animated.View
            entering={FadeIn.duration(animationDuration)}
            exiting={FadeOut.duration(animationDuration)}
            style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(0, 0, 0, 0.3)' }]}
          />
        </Pressable>

        {/* Content - positioned absolutely relative to screen */}
        <Animated.View
          entering={FadeIn.duration(animationDuration)}
          exiting={FadeOut.duration(animationDuration)}
          style={[styles.content, contentStyle, getPositionStyle()]}
          onLayout={handleContentLayout}
          pointerEvents="box-none"
        >
          {/* Arrow indicator */}
          {showArrow && <View style={arrowStyle} />}
          {/* Content */}
          <Pressable onPress={(e) => e.stopPropagation()}>{children}</Pressable>
        </Animated.View>
      </Modal>
    </>
  )
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject
  },
  content: {
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    borderRadius: 12,
    padding: 12,
    minWidth: 200,
    maxWidth: 280,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)'
  }
})
